import { icon }            from '@fortawesome/fontawesome-svg-core';
import { faVideoPlus }     from '@fortawesome/pro-solid-svg-icons/faVideoPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Editor }          from '@tinymce/tinymce-react';
import { PropTypes }       from 'prop-types';
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import GlobalContainer from '~/components/global_container';

import AssistModal from '~/components/modals/AiAssist/Modal';
import IframeExpandThreadButton from '~/components/shared/email/IframeExpandThreadButton';
import { NOOP_FALSE } from '~/constants/callbacks';
import {
  EMAIL_EDITOR_PLUGINS,
  EMAIL_EDITOR_TOOLBAR,
  EMAIL_QUOTE_STYLES,
  IMAGES_UPLOAD_HANDLER,
  TINYMCE_SCRIPT_PATH,
  TINYMCE_SCROLLABLE_UI_OPTIONS,
} from '~/constants/TinyMCE';
import EditorHelpers from '~/helpers/editor_helpers';
import { renderComponent } from '~/helpers/reactRoot';
import { initWebSpellChecker } from '~/lib/web_spell_checker';

const EmailEditor = ({
  id = '',
  value = '',
  className = '',
  placeholder = 'Enter text or insert / drop images ...',
  replaceValue = null,
  clearReplaceValue = NOOP_FALSE,
  leadId = null,
  linksToInsert = [],
  clearLinksToInsert = NOOP_FALSE,
  onChange = NOOP_FALSE,
  setSubject = NOOP_FALSE,
}) => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [showAssistModal, setShowAssistModal] = useState(false);
  const expandedRef = useRef(false);
  const editorRef = useRef(null);

  const handleQuoteToggle = (editor, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!editor || !editor.initialized) {
      return false;
    }

    const body = editor.getBody();
    const quoteContent = body.querySelector('.ss-email-thread__quote');

    if (quoteContent) {
      quoteContent.classList.remove('ss-email-thread__quote--collapsed');
    }

    const toggleWrapper = body.querySelector('.ss-email-thread__toggle-wrapper');
    if (toggleWrapper) {
      toggleWrapper.remove();
    }

    if (editor && editor.nodeChanged) {
      editor.nodeChanged();
    }

    if (editor && editor.execCommand) {
      editor.execCommand('mceRepaint');
    }

    return false;
  };

  const createToggleButton = (editor) => {
    const toggleWrapper = editor.getBody().querySelector('.ss-email-thread__toggle-wrapper');
    if (toggleWrapper) {
      renderComponent(
        <IframeExpandThreadButton
          expanded={expandedRef.current}
          onClick={(e) => {
            handleQuoteToggle(editor, e);
          }}
        />,
        toggleWrapper,
      );
    }
  };

  const initializeContent = (editor) => {
    if (value) {
      editor.setContent(value, { format: 'html' });
      const emailThread = editor.getBody().querySelector('.ss-email-thread');
      if (emailThread) {
        const toggleWrapper = editor.dom.create('div', { class: 'ss-email-thread__toggle-wrapper' });
        emailThread.appendChild(toggleWrapper);
      }
    }
  };

  const setupEditorEvents = (editor) => {
    editor.on('SetContent', () => createToggleButton(editor));
  };

  const handleOnInit = (_event, editor) => {
    editorRef.current = editor;
    setLoading(false);

    initializeContent(editor);
    initWebSpellChecker(editor.iframeElement);
    createToggleButton(editor);
    setupEditorEvents(editor);
  };

  useEffect(() => {
    if (replaceValue) {
      setContent(replaceValue);
      clearReplaceValue();
    }
  }, [replaceValue, clearReplaceValue]);

  useEffect(() => {
    if (linksToInsert?.length) {
      let newContent = content;
      linksToInsert.forEach((link) => {
        const { text, href } = link;
        const linkHtml = `<br/><a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>&nbsp;`;

        newContent += linkHtml;
      });
      setContent(newContent);
      clearLinksToInsert();
    }
  }, [linksToInsert, clearLinksToInsert]);

  const handleEditorChange = (_value, editor) => {
    const newContent = editor.getContent({ format: 'html' });
    setContent(newContent);
    onChange(newContent);
  };

  return (
    <div className={`${className} position-relative`}>
      {loading && (
        <div className="d-flex justify-content-center align-items-center position-absolute w-100 h-100 bg-white z-5">
          <FontAwesomeIcon icon="far fa-spinner" pulse className="mr5" />
          {' '}
          Loading
        </div>
      )}

      <Editor
          id={id}
          tinymceScriptSrc={TINYMCE_SCRIPT_PATH}
          scriptLoading={{ async: true }}
          value={content}
          onEditorChange={handleEditorChange}
          onInit={handleOnInit}
          init={{
            placeholder,
            cache_suffix:                  '?v=7.9.1',
            license_key:                  'gpl',
            ...TINYMCE_SCROLLABLE_UI_OPTIONS,
            plugins:                      EMAIL_EDITOR_PLUGINS,
            toolbar:                      EMAIL_EDITOR_TOOLBAR,
            toolbar_mode:                 'wrap',
            branding:                     false,
            menubar:                      false,
            file_picker_types:            'image',
            min_height:                   350,
            convert_urls:                 false,
            relative_urls:                false,
            remove_script_host:           false,
            document_base_url:            Rails.baseUrl,
            forced_root_block:            'div',
            entity_encoding:              'named',
            link_default_target:          '_blank',
            link_default_protocol:        'https',
            link_target_list:             false,
            content_style:                `
              body { font-family: Arial; font-size: 13px; }
              ${EMAIL_QUOTE_STYLES}
            `,
            paste_as_text:                 false,
            contextmenu:                   false,
            paste_remove_styles_if_webkit: false,
            statusbar:                     true,
            images_upload_handler:         IMAGES_UPLOAD_HANDLER,
            setup:                         (editor) => {
              editor.ui.registry.addIcon('videoIcon', icon(faVideoPlus).html[0]);

              editor.ui.registry.addButton('videos', {
                icon:     'videoIcon',
                tooltip:  'Insert Video',
                onAction: async () => {
                  // Lazy load VideosModal to avoid loading video.js (1.7 MB) on every page
                  const { mountVideosModal } = await import(
                    /* webpackChunkName: "videos-modal" */ '~/components/modals/videos/VideosModal'
                  );
                  mountVideosModal({
                    modalClass:  'modal modal-overlay',
                    dialogClass: 'modal-dialog modal-lg',
                    editor,
                  });
                },
              });

              EditorHelpers.addVariablesMenu(editor);

              editor.ui.registry.addButton('ai-assist', {
                icon:     'ai',
                tooltip:  'AI Assist',
                onAction: () => setShowAssistModal(true),
              });

              EditorHelpers.addVisitLink(editor);
            },
          }}
        />

      {showAssistModal && (
        <AssistModal
          show
          onHide={() => setShowAssistModal(false)}
          editor={editorRef.current}
          setSubject={setSubject}
          editorType="email"
          leadId={leadId}
          product={GlobalContainer.product()}
        />
      )}
    </div>
  );
};

EmailEditor.propTypes = {
  id:                 PropTypes.string,
  value:              PropTypes.string,
  className:          PropTypes.string,
  placeholder:        PropTypes.string,
  replaceValue:       PropTypes.string,
  clearReplaceValue:  PropTypes.func,
  linksToInsert:      PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  })),
  clearLinksToInsert: PropTypes.func,
  onChange:           PropTypes.func,
  leadId:             PropTypes.number,
  setSubject:         PropTypes.func,
};

export default EmailEditor;
